import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import useLocaleDateFormatter from '../../../../hooks/useLocaleDateFormatter';

const VIEWBOX_WIDTH = 320;
const VIEWBOX_HEIGHT = 200;
const MIN_VIEWBOX_WIDTH = 220;
const MIN_VIEWBOX_HEIGHT = 160;
const X_PADDING_RATIO = 0.08;
const TOP_PADDING_RATIO = 0.12;
const BOTTOM_PADDING_RATIO = 0.18;
const MIN_X_PADDING = 12;
const MAX_X_PADDING = 28;
const MIN_TOP_PADDING = 12;
const MAX_TOP_PADDING = 26;
const MIN_BOTTOM_PADDING = 22;
const MAX_BOTTOM_PADDING = 38;
const LONG_RANGE_THRESHOLD_MS = 12 * 60 * 60 * 1000;
const ESTIMATED_CHAR_WIDTH = 6;
const LABEL_GAP = 6;

const TimeSeriesChart = ({
    title,
    data,
    color,
    valueFormatter,
    emptyLabel,
    height,
    yAxisFormatter,
}) => {
    const chartId = useId().replace(/:/g, '');
    const [hoverIndex, setHoverIndex] = useState(null);
    const [dimensions, setDimensions] = useState({
        width: VIEWBOX_WIDTH,
        height: VIEWBOX_HEIGHT,
    });
    const svgRef = useRef(null);
    const { formatDate, formatDateTime, formatTime } = useLocaleDateFormatter();

    useEffect(() => {
        const node = svgRef.current;
        if (!node) {
            return undefined;
        }

        const updateSize = () => {
            const rect = node.getBoundingClientRect();
            const width = Math.max(
                MIN_VIEWBOX_WIDTH,
                Math.round(rect.width || VIEWBOX_WIDTH),
            );
            const height = Math.max(
                MIN_VIEWBOX_HEIGHT,
                Math.round(rect.height || VIEWBOX_HEIGHT),
            );
            setDimensions((current) => {
                if (current.width === width && current.height === height) {
                    return current;
                }
                return { width, height };
            });
        };

        updateSize();

        if (typeof ResizeObserver === 'function') {
            const observer = new ResizeObserver(updateSize);
            observer.observe(node);
            return () => observer.disconnect();
        }

        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    const viewBoxWidth = Math.max(dimensions.width, MIN_VIEWBOX_WIDTH);
    const viewBoxHeight = Math.max(dimensions.height, MIN_VIEWBOX_HEIGHT);
    const xPadding = Math.max(
        MIN_X_PADDING,
        Math.min(MAX_X_PADDING, viewBoxWidth * X_PADDING_RATIO),
    );
    const topPadding = Math.max(
        MIN_TOP_PADDING,
        Math.min(MAX_TOP_PADDING, viewBoxHeight * TOP_PADDING_RATIO),
    );
    const bottomPadding = Math.max(
        MIN_BOTTOM_PADDING,
        Math.min(MAX_BOTTOM_PADDING, viewBoxHeight * BOTTOM_PADDING_RATIO),
    );

    const normalized = useMemo(() => {
        const validSamples = (data || [])
            .filter((sample) => sample && sample.timestamp instanceof Date && !Number.isNaN(sample.timestamp.getTime())
                && sample.value !== null && sample.value !== undefined && Number.isFinite(sample.value))
            .sort((a, b) => a.timestamp - b.timestamp);

        if (!validSamples.length) {
            const toX = () => viewBoxWidth / 2;
            const toY = () => viewBoxHeight - bottomPadding;
            return {
                samples: [],
                linePath: '',
                areaPath: '',
                xLabels: [],
                yTicks: [],
                minTime: 0,
                maxTime: 0,
                timeRange: 0,
                toX,
                toY,
            };
        }

        const minTime = validSamples[0].timestamp.getTime();
        const maxTime = validSamples[validSamples.length - 1].timestamp.getTime();
        const timeRange = Math.max(1, maxTime - minTime);

        const values = validSamples.map((point) => point.value);
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        const rawValueRange = Math.max(1e-9, maxValue - minValue);
        const paddedMin = minValue - rawValueRange * 0.05;
        const paddedMax = maxValue + rawValueRange * 0.05;
        const valueRange = Math.max(1e-9, paddedMax - paddedMin);

        const toX = (time) => {
            if (maxTime === minTime) {
                return viewBoxWidth / 2;
            }
            return xPadding + ((time - minTime) / timeRange) * (viewBoxWidth - 2 * xPadding);
        };

        const usableHeight = Math.max(1, viewBoxHeight - topPadding - bottomPadding);

        const toY = (value) => {
            if (!Number.isFinite(value)) {
                return viewBoxHeight - bottomPadding;
            }
            if (valueRange === 0) {
                return topPadding + usableHeight / 2;
            }
            const ratio = (value - paddedMin) / valueRange;
            const clamped = Math.min(1, Math.max(0, ratio));
            return (viewBoxHeight - bottomPadding) - clamped * usableHeight;
        };

        const linePath = validSamples
            .map((sample, index) => `${index === 0 ? 'M' : 'L'} ${toX(sample.timestamp.getTime())} ${toY(sample.value)}`)
            .join(' ');

        const areaPath = `${linePath} L ${toX(validSamples[validSamples.length - 1].timestamp.getTime())} ${viewBoxHeight - bottomPadding}`
            + ` L ${toX(validSamples[0].timestamp.getTime())} ${viewBoxHeight - bottomPadding} Z`;

        const xLabels = buildXLabels(validSamples, toX);
        const yTicks = buildYTicks(paddedMin, paddedMax, toY);

        return {
            samples: validSamples,
            linePath,
            areaPath,
            xLabels,
            yTicks,
            minTime,
            maxTime,
            timeRange,
            toX,
            toY,
        };
    }, [data, bottomPadding, topPadding, viewBoxHeight, viewBoxWidth, xPadding]);

    const valueFormatterFn = valueFormatter || defaultValueFormatter;
    const yFormatter = yAxisFormatter || valueFormatterFn;

    const formatAxisLabel = useCallback((timestamp) => {
        if (!timestamp) {
            return '';
        }
        const showDate = normalized.timeRange > LONG_RANGE_THRESHOLD_MS;
        if (showDate) {
            const date = formatDate(timestamp, { month: 'short', day: 'numeric' });
            const time = formatTime(timestamp, { minute: '2-digit' });
            return `${date} ${time}`;
        }
        return formatTime(timestamp, { minute: '2-digit' });
    }, [formatDate, formatTime, normalized.timeRange]);

    const formatTooltipTimestamp = useCallback(
        (timestamp) => formatDateTime(timestamp, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }),
        [formatDateTime],
    );

    const xLabels = useMemo(() => {
        if (!normalized.xLabels.length) {
            return [];
        }
        const labels = normalized.xLabels
            .map((label) => ({
                ...label,
                text: formatAxisLabel(label.timestamp),
            }))
            .filter((label) => Boolean(label.text));

        if (labels.length <= 2) {
            return labels;
        }

        const [first, middle, last] = labels;
        const firstWidth = Math.max(28, first.text.length * ESTIMATED_CHAR_WIDTH);
        const middleWidth = Math.max(28, middle.text.length * ESTIMATED_CHAR_WIDTH);
        const lastWidth = Math.max(28, last.text.length * ESTIMATED_CHAR_WIDTH);

        const hasRoomForMiddle = (middle.x - first.x) >= ((firstWidth / 2) + (middleWidth / 2) + LABEL_GAP)
            && (last.x - middle.x) >= ((middleWidth / 2) + (lastWidth / 2) + LABEL_GAP);

        if (!hasRoomForMiddle) {
            return [first, last];
        }

        if ((last.x - first.x) < ((firstWidth / 2) + (lastWidth / 2) + LABEL_GAP)) {
            return [last];
        }

        return labels;
    }, [formatAxisLabel, normalized.xLabels]);

    const handleMouseMove = useCallback((event) => {
        if (!normalized.samples.length) {
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        if (!rect.width) {
            return;
        }
        const ratio = (event.clientX - rect.left) / rect.width;
        const clamped = Math.min(1, Math.max(0, ratio));
        const targetTime = normalized.minTime + clamped * normalized.timeRange;
        let nearestIndex = 0;
        let smallestDiff = Number.POSITIVE_INFINITY;
        normalized.samples.forEach((sample, index) => {
            const diff = Math.abs(sample.timestamp.getTime() - targetTime);
            if (diff < smallestDiff) {
                smallestDiff = diff;
                nearestIndex = index;
            }
        });
        setHoverIndex(nearestIndex);
    }, [normalized]);

    const handleMouseLeave = useCallback(() => {
        setHoverIndex(null);
    }, []);

    const hoveredSample = hoverIndex !== null && normalized.samples[hoverIndex]
        ? normalized.samples[hoverIndex]
        : null;
    const hoverX = hoveredSample ? normalized.toX(hoveredSample.timestamp.getTime()) : null;
    const hoverXPercent = hoverX !== null ? (hoverX / viewBoxWidth) * 100 : null;
    const hoverY = hoveredSample ? normalized.toY(hoveredSample.value) : null;

    const containerStyle = height ? { height: typeof height === 'number' ? `${height}px` : height } : undefined;

    return (
        <div className="time-series-chart" style={containerStyle}>
            <div className="time-series-chart__header">
                <h4 className="time-series-chart__title">{title}</h4>
                {hoveredSample && (
                    <div className="time-series-chart__legend">
                        <span className="time-series-chart__legend-value">{valueFormatterFn(hoveredSample.value)}</span>
                        <span className="time-series-chart__legend-time">{formatTooltipTimestamp(hoveredSample.timestamp)}</span>
                    </div>
                )}
            </div>
            <div className="time-series-chart__canvas">
                {normalized.samples.length ? (
                    <>
                        <svg
                            viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
                            preserveAspectRatio="none"
                            className="time-series-chart__svg"
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                            role="img"
                            aria-label={`${title} timeseries chart`}
                            ref={svgRef}
                        >
                            <defs>
                                <linearGradient id={`${chartId}-fill`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={color} stopOpacity="0.32" />
                                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <rect
                                x={xPadding}
                                y={topPadding}
                                width={viewBoxWidth - 2 * xPadding}
                                height={viewBoxHeight - topPadding - bottomPadding}
                                className="time-series-chart__grid"
                            />
                            <path d={normalized.areaPath} fill={`url(#${chartId}-fill)`} />
                            <path d={normalized.linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
                            <line
                                x1={xPadding}
                                y1={viewBoxHeight - bottomPadding}
                                x2={viewBoxWidth - xPadding}
                                y2={viewBoxHeight - bottomPadding}
                                className="time-series-chart__axis"
                            />
                            <line
                                x1={xPadding}
                                y1={topPadding}
                                x2={xPadding}
                                y2={viewBoxHeight - bottomPadding}
                                className="time-series-chart__axis"
                            />
                            {normalized.yTicks.map((tick) => (
                                <g key={tick.value}>
                                    <line
                                        x1={xPadding}
                                        x2={viewBoxWidth - xPadding}
                                        y1={tick.y}
                                        y2={tick.y}
                                        className="time-series-chart__grid-line"
                                    />
                                    <text
                                        x={xPadding - 1.5}
                                        y={tick.y}
                                        className="time-series-chart__axis-label"
                                        textAnchor="end"
                                        dominantBaseline="middle"
                                    >
                                        {yFormatter(tick.value)}
                                    </text>
                                </g>
                            ))}
                            {xLabels.map((label) => (
                                <text
                                    key={label.timestamp.getTime()}
                                    x={label.x}
                                    y={viewBoxHeight - bottomPadding + 4}
                                    className="time-series-chart__axis-label time-series-chart__axis-label--x"
                                    textAnchor="middle"
                                >
                                    {label.text}
                                </text>
                            ))}
                            {hoveredSample && (
                                <g className="time-series-chart__hover">
                                    <line
                                        x1={hoverX}
                                        x2={hoverX}
                                        y1={topPadding}
                                        y2={viewBoxHeight - bottomPadding}
                                        className="time-series-chart__hover-line"
                                    />
                                    <circle
                                        cx={hoverX}
                                        cy={hoverY}
                                        r={1.8}
                                        className="time-series-chart__hover-point"
                                    />
                                </g>
                            )}
                        </svg>
                        {hoveredSample && hoverXPercent !== null && (
                            <div
                                className="time-series-chart__tooltip"
                                style={{ left: `${hoverXPercent}%` }}
                            >
                                <div className="time-series-chart__tooltip-value">{valueFormatterFn(hoveredSample.value)}</div>
                                <div className="time-series-chart__tooltip-time">{formatTooltipTimestamp(hoveredSample.timestamp)}</div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="time-series-chart__empty">{emptyLabel}</div>
                )}
            </div>
        </div>
    );
};

TimeSeriesChart.propTypes = {
    title: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
        timestamp: PropTypes.instanceOf(Date).isRequired,
        value: PropTypes.number,
    })),
    color: PropTypes.string,
    valueFormatter: PropTypes.func,
    emptyLabel: PropTypes.string,
    height: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    yAxisFormatter: PropTypes.func,
};

TimeSeriesChart.defaultProps = {
    data: [],
    color: 'var(--color-accent-indigo, #607bff)',
    valueFormatter: undefined,
    emptyLabel: 'Metric data unavailable for the selected range.',
    height: 240,
    yAxisFormatter: undefined,
};

const defaultValueFormatter = (value) => {
    if (value === null || value === undefined) {
        return 'N/A';
    }
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
};

const buildXLabels = (samples, toX) => {
    if (!samples.length) {
        return [];
    }
    const indices = [0, Math.floor(samples.length / 2), samples.length - 1];
    const seen = new Set();
    const labels = [];
    indices.forEach((index) => {
        const sample = samples[index];
        if (!sample) {
            return;
        }
        const key = sample.timestamp.getTime();
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        labels.push({
            timestamp: sample.timestamp,
            x: toX(sample.timestamp.getTime()),
        });
    });
    labels.sort((a, b) => a.timestamp - b.timestamp);
    return labels;
};

const buildYTicks = (min, max, toY) => {
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return [];
    }
    const ticks = [min, (min + max) / 2, max];
    const result = [];
    ticks.forEach((tick) => {
        if (!Number.isFinite(tick)) {
            return;
        }
        if (result.some((existing) => Math.abs(existing.value - tick) < 1e-6)) {
            return;
        }
        result.push({ value: tick, y: toY(tick) });
    });
    result.sort((a, b) => b.value - a.value);
    return result;
};

export default TimeSeriesChart;
